import get from 'lodash/get';
import camelCase from 'lodash/camelCase';
import {DEFAULT_VIEWS, DecorationMethod} from '~/constants/ProductConstants';
import remoteServices from '~/constants/RemoteServices';
import {recordFromMmsColor} from '~/data/records/Color';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';
import {sanitizeUrl} from '~/utils/url';

const colors = {};
export function getColor(style, colorId) {
  if (colorId in colors && colors[colorId]) return colors[colorId];
  if (!style || !style.colors) return undefined;

  const color = style.colors.find(c => c.id === colorId);
  colors[colorId] = color;
  return color;
}

export function getViewImage(color, viewName) {
  return color?.view_images?.find(vi => camelCase(vi?.name) === viewName);
}

export function getViewImageWithUri(color, viewName) {
  const viewImage = getViewImage(color, viewName);
  return viewImage?.uri ? viewImage : null;
}

export function getViewName(viewName, truncated) {
  if (viewName === 'leftSleeve') {
    viewName = truncated ? 'L.Sleeve' : 'Left Sleeve';
  } else if (viewName === 'rightSleeve') {
    viewName = truncated ? 'R.Sleeve' : 'Right Sleeve';
  }
  return viewName;
}

export function getMultiItemCategoriesPath({
  currentProductColorsArray,
  designDigest
}) {
  const colors = 'preferred_colors';
  const colorsParam = currentProductColorsArray?.reduce((acc, color) => {
    acc.push(`${colors}[]=${color.simple_names[0]}`);
    return acc;
  }, []);
  if (colorsParam?.length) {
    return `/categories?${colorsParam.join('&')}&digest=${designDigest}`;
  }
  return `/categories?digest=${designDigest}`;
}
export function getCategoryPath({category, categoryId}) {
  if (!category || !categoryId) return null;
  return `/categories/${sanitizeUrl(category)}/${categoryId}/styles`;
}

export function getNormalizedCategoryPath({categoryPath}) {
  if (!categoryPath) return '';
  const catalogDomain = (remoteServices?.catalog || '').replace(
    /^\w*:?\/\/[^/]+/,
    ''
  );
  return categoryPath
    ? categoryPath.replace(new RegExp(`^${catalogDomain}`), '')
    : '';
}

export function getOverrideCategoryPath({override}) {
  if (!override || !override.match(/\?pathoverride=/)) return null;
  const match = override.match(/\?pathoverride=(.+)/);
  return match ? `/${match[1]}` : null;
}

export function sortColors(product) {
  if (!product) return [];
  const {colors = []} = product;
  return colors.sort((a, b) => a.priority - b.priority);
}

export function mapToColorRecord(colors) {
  return colors.map(recordFromMmsColor);
}

export function getAdditionalProductViews(product) {
  return (
    product && product.views.filter(view => !DEFAULT_VIEWS.includes(view.name))
  );
}

export function isConsistentStyleCategoryPath({
  categoryPath,
  primaryCategoryId
}) {
  const categoryPathParts = categoryPath?.split('/');

  if (
    !categoryPathParts ||
    categoryPathParts.length === 0 ||
    !primaryCategoryId
  ) {
    return false;
  }
  return (
    parseInt(categoryPathParts[categoryPathParts.length - 1], 10) ===
    parseInt(primaryCategoryId, 10)
  );
}

export function isMobile() {
  return !breakpoint(MediaQueries.largeAndAbove);
}

// size has stock_status and keys
export function isAvailable(size) {
  return (
    size.status === 'active' &&
    (size.stock_status === 'in' || size.stock_status === 'low')
  );
}

// Finds all variants similar to the current product
export const findMatchingVariants = product => {
  const currentVariant = product.variants.find(
    v => v.style_id === parseInt(product.id, 10)
  );
  return product.variants.filter(
    v =>
      v.product_type === product.product_type &&
      v.status === product.status &&
      v.region === currentVariant?.region
  );
};

// As of now, "having an eligbible matching" variant, means screen print & embroidery only
export const hasEligibleMatchingVariants = product => {
  if (get(product, 'variants.length', 0) < 2) {
    return false;
  }

  const matchingVariants = findMatchingVariants(product);
  if (!matchingVariants.length) return false;
  return !!(
    matchingVariants.find(
      v => v.decoration_method === DecorationMethod.Embroidery
    ) &&
    matchingVariants.find(
      v => v.decoration_method === DecorationMethod.ScreenPrint
    )
  );
};

// If current product is "screen print", this will get variant "embroidery" & vice-versa
export const getAlternateVariant = product => {
  if (get(product, 'variants.length', 0) < 2) {
    return null;
  }

  const matchingVariants = findMatchingVariants(product);
  switch (product.decoration_method) {
    case DecorationMethod.ScreenPrint:
      return matchingVariants.find(
        v => v.decoration_method === DecorationMethod.Embroidery
      );
    case DecorationMethod.Embroidery:
      return matchingVariants.find(
        v => v.decoration_method === DecorationMethod.ScreenPrint
      );
    default:
      return null;
  }
};

export const ELIGIBLE_DECO_FLEX_DECORATION_METHODS = new Set([
  DecorationMethod.ScreenPrint,
  DecorationMethod.Embroidery
]);

export const isEligibleForDecoFlex = () => false;
